import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Alert,
  Badge,
  Card,
  DateInput,
  Modal,
  NumberInput,
  RadioGroupInput,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import {
  type CommandAdminApi,
  commandStoreApi,
  CommissionPayeeTypeEnum,
} from 'corso-types';
import { type FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { formatter } from 'corso-web-lib';

type Store = CommandAdminApi.ResponseBody<'/store/:storeId', 'get'>;
type CommissionArrangement = Store['commissionArrangements'][number];

const createArrangementSchema =
  commandStoreApi['/store/:storeId/commission-arrangement'].post.request.body;
const updateArrangementSchema =
  commandStoreApi[
    '/store/:storeId/commission-arrangement/:commissionArrangementId'
  ].put.request.body;

const payeeTypeLabels = {
  [CommissionPayeeTypeEnum.partner]: 'Partner',
  [CommissionPayeeTypeEnum.accountExecutive]: 'Account Executive',
  [CommissionPayeeTypeEnum.merchant]: 'Merchant',
} as const satisfies Record<CommissionPayeeTypeEnum, string>;

const methodLabels = {
  Topline: 'Platform (Topline)',
  Traditional: 'Shipping Protection (Traditional)',
} as const satisfies Record<
  (typeof createArrangementSchema.shape.method.options)[number],
  string
>;

const methodOptions = createArrangementSchema.shape.method.options.map(
  (value) => ({ value, label: methodLabels[value] }),
);

const collectionMethodOptions = {
  // ! isDeductedFromInvoice = true
  Deducted: 'The brand keeps this amount - (deducted from invoice)',
  // ! isDeductedFromInvoice = false
  Postpaid: 'Corso collects the amount and will pay via ACH, check, etc.',
} as const satisfies Record<
  (typeof createArrangementSchema.shape.collectionMethod.options)[number],
  string
>;

const { idColumn, createdOnColumn } = sharedTableDefinitions;

const handleDate = (value: string | null) =>
  value ? formatter.date(value) : '—';

const payeeTypeOptions = createArrangementSchema.shape.payeeType.options.map(
  (value) => ({ value, label: payeeTypeLabels[value] }),
);

function AddCommissionArrangementModal({ onClose }: { onClose: () => void }) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createArrangementSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      rate: 0,
      method: 'Topline' as const,
      collectionMethod: 'Postpaid' as const,
      notes: null,
      payeeName: '',
      payeeType: CommissionPayeeTypeEnum.partner,
    },
  });

  const {
    mutate: createArrangement,
    isPending,
    error,
  } = useMutation({
    mutationFn: (
      arrangement: CommandAdminApi.RequestBody<
        '/store/:storeId/commission-arrangement',
        'post'
      >,
    ) =>
      api.store(storeId).createCommissionArrangement({
        ...arrangement,
        rate: arrangement.rate / 100,
      }),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Created commission arrangement');
    },
    onError: () => toast.show('Failed to create commission arrangement'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((arrangement) => createArrangement(arrangement))(event).catch(
      console.error,
    );
  };

  return (
    <Modal show onClose={onClose} title="Add Commission Arrangement">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="startDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="startDate"
              label="Start Date"
              required
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="endDate"
              label="End Date"
              required
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        <NumberInput
          id="rate"
          label="Rate"
          required
          step="any"
          addon={{ insideEnd: '%' }}
          {...register('rate', { valueAsNumber: true })}
          error={errors.rate?.message}
        />
        <Controller
          control={control}
          name="method"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Method"
              required
              options={methodOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="collectionMethod"
          render={({ field, fieldState }) => (
            <RadioGroupInput
              id="collectionMethod"
              label="How will the funds be dispersed?"
              options={collectionMethodOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <TextInput
          id="notes"
          label="Notes"
          {...register('notes')}
          error={errors.notes?.message}
        />
        <Card.Line>
          <Card.Heading>Who will be paid?</Card.Heading>
        </Card.Line>
        <TextInput
          id="payeeName"
          label="Payee Name"
          required
          {...register('payeeName')}
          error={errors.payeeName?.message}
        />
        <Controller
          control={control}
          name="payeeType"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Payee Type"
              required
              options={payeeTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        {error ? (
          <Alert heading="Critical Configuration Error" variant="critical">
            {error.message}
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Action variant="primary" type="submit" loading={isPending}>
            Add
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function EditCommissionArrangementModal({
  arrangement,
  onClose,
}: {
  arrangement: CommissionArrangement;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const { control, handleSubmit } = useForm({
    resolver: zodResolver(updateArrangementSchema),
    defaultValues: {
      startDate: arrangement.startDate,
      endDate: arrangement.endDate ?? '',
    },
  });

  const {
    mutate: updateArrangement,
    isPending,
    error,
  } = useMutation({
    mutationFn: (
      body: CommandAdminApi.RequestBody<
        '/store/:storeId/commission-arrangement/:commissionArrangementId',
        'put'
      >,
    ) => api.store(storeId).updateCommissionArrangement(arrangement.id, body),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated commission arrangement');
    },
    onError: () => toast.show('Failed to update commission arrangement'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateArrangement(body))(event).catch(console.error);
  };

  return (
    <Modal show onClose={onClose} title="Edit Commission Arrangement">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="startDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="startDate"
              label="Start Date"
              required
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endDate"
          render={({ field, fieldState }) => (
            <DateInput
              id="endDate"
              label="End Date"
              required
              value={field.value || null}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />

        {error ? (
          <Alert heading="Critical Configuration Error" variant="critical">
            {error.message}
          </Alert>
        ) : null}
        <div className="flex justify-end">
          <Action variant="primary" type="submit" loading={isPending}>
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

export function CommissionArrangementsCard() {
  const { commissionArrangements } = useStoreContext();
  const [showAdd, setShowAdd] = useState(false);
  const [arrangementToEdit, setArrangementToEdit] =
    useState<CommissionArrangement | null>(null);

  const columns: ColumnDef<CommissionArrangement>[] = [
    idColumn<CommissionArrangement>({ meta: { align: 'left' } }),
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'default'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    { accessorKey: 'payeeName', header: 'Payee', enableSorting: true },
    { accessorKey: 'payeeType', header: 'Type', enableSorting: true },
    {
      accessorKey: 'rate',
      header: 'Rate',
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ row }) => formatter.percent(row.original.rate),
    },

    {
      accessorKey: 'method',
      header: 'Method',
      cell: ({ row }) => methodLabels[row.original.method],
    },
    {
      accessorKey: 'collectionMethod',
      header: 'Collection',
      cell: ({ row }) =>
        row.original.collectionMethod === 'Deducted'
          ? 'Deducted from Invoice'
          : 'Direct Payment',
    },
    {
      accessorKey: 'startDate',
      header: 'Start',
      enableSorting: true,
      cell: ({ row }) => handleDate(row.original.startDate),
    },
    {
      accessorKey: 'endDate',
      header: 'End',
      cell: ({ row }) => handleDate(row.original.endDate),
    },

    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => row.original.notes || '—',
    },
    createdOnColumn<CommissionArrangement>(),
    {
      id: 'actions',
      header: '',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Action
          variant="ghost"
          icon="edit"
          accessibilityLabel="Edit Commission Arrangement"
          onClick={() => setArrangementToEdit(row.original)}
        />
      ),
    },
  ];

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Commission Arrangements</Card.Heading>
        <Action
          variant="ghost"
          icon="add"
          accessibilityLabel="Add Commission Arrangement"
          onClick={() => setShowAdd(true)}
        />
      </Card.Line>

      {commissionArrangements.length > 0 ? (
        <DataTable
          data={commissionArrangements}
          columns={columns}
          pageSize={10}
          defaultSort={[{ id: 'isActive', desc: true }]}
        />
      ) : (
        <DataTableStatus
          icon="receipt"
          title="No commission arrangements"
          details="Commission arrangements will appear here once configured for this store."
        />
      )}

      {showAdd && (
        <AddCommissionArrangementModal onClose={() => setShowAdd(false)} />
      )}

      {arrangementToEdit && (
        <EditCommissionArrangementModal
          arrangement={arrangementToEdit}
          onClose={() => setArrangementToEdit(null)}
        />
      )}
    </Card>
  );
}
